import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';

import { Product } from '../../models/Product';
import { useProductGroup } from '../../context/ProductGroupContext';
import ProductCard from '../ProductCard';

interface SuggestedProductsProps {
  productId: number;
}

const SuggestedProducts: React.FC<SuggestedProductsProps> = ({ productId }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getSuggestedProducts } = useProductGroup();

  useEffect(() => {
    const fetchSuggestedProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const suggestedProducts = await getSuggestedProducts(productId, 5);
        setProducts(suggestedProducts);
      } catch (err: any) {
        console.error('Error fetching suggested products:', err);
        setError('Nie udało się załadować sugerowanych produktów');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestedProducts();
  }, [productId, getSuggestedProducts]);

  // If there are no products and we're not loading, don't render anything
  if (!isLoading && products.length === 0) {
    return null;
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="mb-12"
    >
      <motion.div
        variants={itemVariants}
        className="flex justify-between items-center mb-6"
      >
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <ViewCarouselIcon className="mr-2" /> Polecane produkty
        </h2>
      </motion.div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse bg-white rounded-lg border border-gray-200 shadow-sm h-72"
            >
              <div className="h-40 bg-gray-200 rounded-t-lg"></div>
              <div className="p-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-full mt-4"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <motion.div
          className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700"
          variants={itemVariants}
        >
          {error}
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {products.map((product, index) => (
            <ProductCard
              product={product}
              key={index}
              vertical={true}
              className="w-full"
            />
          ))}
        </div>
      )}
    </motion.section>
  );
};

export default SuggestedProducts;
